import type { ExtractedFact } from '@karehero/models';

// Topics and facts the extractor reads, by the names it gives them.

/** The care recipient's details. */
export const PERSONAL_DETAILS_TOPIC = 'personal_details';
export const MEDICAL_TOPIC = 'medical';
/** The details the person filling in the form gives about themselves. */
export const PRIMARY_CAREGIVER_TOPIC = 'primary_caregiver';

export const FULL_NAME_FACT = 'full_name';
export const FIRST_NAME_FACT = 'first_name';
export const LAST_NAME_FACT = 'last_name';
export const DATE_OF_BIRTH_FACT = 'date_of_birth';
export const NI_NUMBER_FACT = 'ni_number';
export const GP_FACT = 'gp_name_or_practice';
export const HOSPITAL_STAYS_FACT = 'hospital_admissions_past_12_months';
export const PHONE_NUMBER_FACT = 'phone_number';
export const CARE_IS_FOR_SELF_FACT = 'care_is_for_self';

/** The fact read for this topic and name, unless dismissed. */
export const findFact = (
  facts: readonly ExtractedFact[],
  topicSlug: string,
  factName: string,
): ExtractedFact | undefined =>
  facts.find(
    (f) =>
      f.topicSlug === topicSlug && f.factName === factName && !f.dismissedAt,
  );

/** Whether what they said makes the care for themselves; unknown until it is clear. */
export const isCareForSelf = (
  facts: readonly ExtractedFact[],
): boolean | undefined => {
  const fact = findFact(facts, PERSONAL_DETAILS_TOPIC, CARE_IS_FOR_SELF_FACT);
  return typeof fact?.factValue === 'boolean' ? fact.factValue : undefined;
};

/** A fact's value as the option ids of a care profile question; none for a value that is none. */
export const optionIDsOf = ({ factValue }: ExtractedFact): string[] => {
  if (typeof factValue === 'boolean') return [String(factValue)];
  if (typeof factValue === 'string') return factValue ? [factValue] : [];
  if (Array.isArray(factValue)) {
    return factValue.filter((v): v is string => typeof v === 'string');
  }
  return [];
};

/** Whether an answer to the question was read from any of these messages, by the message or the document it sent. */
export const isAnsweredFrom = (
  facts: readonly ExtractedFact[],
  messages: readonly { id: string; documentId?: string | null }[],
  questionID: string,
): boolean =>
  facts.some(
    (fact) =>
      !fact.dismissedAt &&
      optionIDsOf(fact).length > 0 &&
      fact.mappings.some((m) => m.questionId === questionID) &&
      messages.some(
        (m) =>
          (!!fact.messageId && fact.messageId === m.id) ||
          (!!fact.documentId && fact.documentId === m.documentId),
      ),
  );

/** Whether their own phone number has been read. */
export const isPhoneKnown = (facts: readonly ExtractedFact[]): boolean =>
  !!findFact(facts, PRIMARY_CAREGIVER_TOPIC, PHONE_NUMBER_FACT)?.factValue;
